import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, Users, Briefcase, Pencil, Trash2, Save, Search, RefreshCcw, KeyRound, Mail, ToggleLeft, ToggleRight, Loader2, AlertCircle, CheckCircle2, Shield, Plus, GitBranch, Globe, CheckCircle, Upload, XCircle, Download, Archive, ArchiveRestore } from 'lucide-react';
import { getAllClients, createClient, updateClient, deleteClient, getArchivedClients, archiveClient, unarchiveClient, type ClientItem } from '../services/clientService';
import { type PartiePrenante, getPartiesPrenantes, updatePartiePrenante, createPartiePrenante, deletePartiePrenante, getPartiePrenanteAvatarUrl, downloadPartiePrenanteAvatar } from '../services/partiesPrenantesService';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './ConfirmModal';
import {
  adminResetMfa,
  fetchMfaAdminStatus,
  canManageMfaReset,
  isMfaSubjectPartiePrenante,
  type MfaPilotStatus,
} from '../services/authService';

interface AdminModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

export default function AdminModal({ isOpen, onClose }: AdminModalProps) {
  const [activeTab, setActiveTab] = useState<'clients' | 'pp'>('clients');
  const [toasts, setToasts] = useState<Toast[]>([]);

  const pushToast = (type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts((t) => [...t, { id, type, message }]);
    setTimeout(() => setToasts((t) => t.filter(to => to.id !== id)), 4000);
  };

  // Vérifier que document.body existe avant de créer le portal
  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[1600px] h-[92vh] overflow-hidden pointer-events-auto flex flex-col">
              {/* Header avec gradient */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-6 z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                      <Shield className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">Administration</h2>
                      <p className="text-sm text-white/90 font-medium">Accès BZ Inc. uniquement</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('clients')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      activeTab === 'clients'
                        ? 'bg-white text-blue-600 shadow-lg'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    Clients
                  </button>
                  <button
                    onClick={() => setActiveTab('pp')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      activeTab === 'pp'
                        ? 'bg-white text-blue-600 shadow-lg'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    Parties prenantes
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {activeTab === 'clients' ? (
                      <ClientsAdmin pushToast={pushToast} />
                    ) : (
                      <PPAdmin pushToast={pushToast} />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Toasts animés */}
          <div className="pointer-events-none fixed right-6 bottom-6 z-[10000] space-y-2">
            <AnimatePresence>
              {toasts.map((toast) => (
                <motion.div
                  key={toast.id}
                  initial={{ opacity: 0, x: 100, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 100, scale: 0.9 }}
                  className={`pointer-events-auto rounded-lg shadow-xl px-4 py-3 text-sm text-white flex items-center gap-2 min-w-[300px] ${
                    toast.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
                  }`}
                >
                  {toast.type === 'success' ? (
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  )}
                  <span>{toast.message}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

// --- Sous-composants identiques à PRIsME_Eleganza ---

function ClientFormModal({
  isOpen,
  onClose,
  client,
  onSave,
  pushToast
}: {
  isOpen: boolean;
  onClose: () => void;
  client: ClientItem | null;
  onSave: () => void;
  pushToast: (type: 'success' | 'error', msg: string) => void;
}) {
  const [form, setForm] = useState<ClientItem>({
    nom: '',
    secteur: '',
    taille_entreprise: '',
    nombre_employes: undefined,
    enjeux_principaux: '',
    gitrepository: '',
    prismewebsite: '',
    prismecompleted: false,
    id_client_erp: ''
  });
  const [saving, setSaving] = useState(false);
  const [archiveStep, setArchiveStep] = useState<0 | 1 | 2>(0);
  const [archiveConfirmChecked, setArchiveConfirmChecked] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        nom: client.nom || '',
        secteur: client.secteur || '',
        taille_entreprise: client.taille_entreprise || '',
        nombre_employes: client.nombre_employes,
        enjeux_principaux: client.enjeux_principaux || '',
        gitrepository: client.gitrepository || '',
        prismewebsite: client.prismewebsite || '',
        prismecompleted: client.prismecompleted ?? false,
        id_client_erp: client.id_client_erp || ''
      });
    } else {
      setForm({
        nom: '',
        secteur: '',
        taille_entreprise: '',
        nombre_employes: undefined,
        enjeux_principaux: '',
        gitrepository: '',
        prismewebsite: '',
        prismecompleted: false,
        id_client_erp: ''
      });
    }
  }, [client, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setArchiveStep(0);
      setArchiveConfirmChecked(false);
    }
  }, [isOpen]);

  const handleArchiveConfirm = async () => {
    if (!client?.id || archiveStep !== 2 || !archiveConfirmChecked) return;
    setArchiving(true);
    try {
      await archiveClient(client.id);
      pushToast('success', 'Client archivé avec succès');
      onSave();
      onClose();
      setArchiveStep(0);
      setArchiveConfirmChecked(false);
    } catch (err: any) {
      pushToast('error', err.message || 'Erreur lors de l\'archivage');
    } finally {
      setArchiving(false);
    }
  };

  const handleSave = async () => {
    if (!form.nom.trim()) {
      pushToast('error', 'Le nom du client est requis');
      return;
    }

    setSaving(true);
    try {
      const clientData: ClientItem = {
        nom: form.nom.trim(),
        secteur: form.secteur?.trim() || undefined,
        taille_entreprise: form.taille_entreprise?.trim() || undefined,
        nombre_employes: form.nombre_employes || undefined,
        enjeux_principaux: form.enjeux_principaux?.trim() || undefined,
        gitrepository: form.gitrepository?.trim() || undefined,
        prismewebsite: form.prismewebsite?.trim() || undefined,
        prismecompleted: form.prismecompleted ?? undefined,
        id_client_erp: form.id_client_erp?.trim() || undefined
      };

      if (client?.id) {
        await updateClient(client.id, clientData);
        pushToast('success', 'Client modifié avec succès');
      } else {
        await createClient(clientData);
        pushToast('success', 'Client créé avec succès');
      }
      onSave();
      onClose();
    } catch (err: any) {
      pushToast('error', err.message || (client ? 'Erreur lors de la modification du client' : 'Erreur lors de la création du client'));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto">
              <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">
                    {client ? 'Modifier le client' : 'Nouveau client'}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du client *
                  </label>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSave()}
                    placeholder="Nom du client"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                    disabled={saving}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secteur
                    </label>
                    <input
                      type="text"
                      value={form.secteur || ''}
                      onChange={(e) => setForm({ ...form, secteur: e.target.value })}
                      placeholder="Ex: Services TI, Syndicat, etc."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ID client ERP
                    </label>
                    <input
                      type="text"
                      value={form.id_client_erp || ''}
                      onChange={(e) => setForm({ ...form, id_client_erp: e.target.value })}
                      placeholder="ID client dans l'ERP"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taille d'entreprise
                    </label>
                    <select
                      value={form.taille_entreprise || ''}
                      onChange={(e) => setForm({ ...form, taille_entreprise: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={saving}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="TPE">TPE (Très Petite Entreprise)</option>
                      <option value="PME">PME (Petite et Moyenne Entreprise)</option>
                      <option value="ETI">ETI (Entreprise de Taille Intermédiaire)</option>
                      <option value="GE">GE (Grande Entreprise)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre d'employés
                  </label>
                  <input
                    type="number"
                    value={form.nombre_employes || ''}
                    onChange={(e) => setForm({ ...form, nombre_employes: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder="Ex: 2500"
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enjeux principaux
                  </label>
                  <textarea
                    value={form.enjeux_principaux || ''}
                    onChange={(e) => setForm({ ...form, enjeux_principaux: e.target.value })}
                    placeholder="Décrivez les principaux enjeux du client..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={saving}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dépôt Git PRIsME
                    </label>
                    <input
                      type="url"
                      value={form.gitrepository || ''}
                      onChange={(e) => setForm({ ...form, gitrepository: e.target.value })}
                      placeholder="https://github.com/..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site PRIsME
                    </label>
                    <input
                      type="url"
                      value={form.prismewebsite || ''}
                      onChange={(e) => setForm({ ...form, prismewebsite: e.target.value })}
                      placeholder="https://client.prisme.ai/..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      PRIsME complété
                    </p>
                    <p className="text-xs text-gray-500">
                      Indique si le livrable PRIsME pour ce client est finalisé.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, prismecompleted: !form.prismecompleted })}
                    className="flex items-center gap-2 rounded-full px-3 py-1.5 border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                    disabled={saving}
                  >
                    {form.prismecompleted ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    <span className="text-sm font-medium">
                      {form.prismecompleted ? 'Oui' : 'Non'}
                    </span>
                  </button>
                </div>

                {client && (
                  <div className="pt-2 border-t border-gray-200 space-y-1 text-xs text-gray-500">
                    {client.date_creation && (
                      <p>Créé le: {new Date(client.date_creation).toLocaleDateString('fr-CA')}</p>
                    )}
                    {client.date_modification && (
                      <p>Modifié le: {new Date(client.date_modification).toLocaleDateString('fr-CA')}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {client && client.id !== 2 && (
                    <button
                      type="button"
                      onClick={() => setArchiveStep(1)}
                      disabled={saving}
                      className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                    >
                      <Archive className="w-4 h-4" />
                      Archiver le client
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={onClose}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !form.nom.trim()}
                    className="px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium hover:from-blue-700 hover:to-blue-600 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {saving ? 'Enregistrement...' : 'Sauvegarder'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Double validation : étape 1 - Confirmation d'intention */}
          {archiveStep === 1 && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black/60 z-[10001]"
                onClick={() => setArchiveStep(0)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none"
              >
                <div
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="bg-amber-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-amber-900 mb-2">Archiver ce client ?</h4>
                    <p className="text-sm text-amber-800">
                      Le client n&apos;apparaîtra plus dans les listes actives (Clients, Parties prenantes, etc.).
                      Vous pourrez le consulter dans « Clients archivés » depuis l&apos;Administration.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setArchiveStep(0)}
                      className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 font-medium"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={() => setArchiveStep(2)}
                      className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 font-medium"
                    >
                      Continuer
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}

          {/* Double validation : étape 2 - Case à cocher */}
          {archiveStep === 2 && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black/60 z-[10002]"
                onClick={() => setArchiveStep(0)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="fixed inset-0 z-[10002] flex items-center justify-center p-4 pointer-events-none"
              >
                <div
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="bg-amber-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-amber-900 mb-2">Confirmer l&apos;archivage</h4>
                    <p className="text-sm text-amber-800 mb-3">
                      Pour confirmer, cochez la case ci-dessous puis cliquez sur « Confirmer l&apos;archivage ».
                    </p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={archiveConfirmChecked}
                        onChange={(e) => setArchiveConfirmChecked(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-amber-900">
                        Je confirme l&apos;archivage du client <strong>{client?.nom}</strong>.
                      </span>
                    </label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => { setArchiveStep(0); setArchiveConfirmChecked(false); }}
                      className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 font-medium"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleArchiveConfirm}
                      disabled={!archiveConfirmChecked || archiving}
                      className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 font-medium disabled:opacity-50 flex items-center gap-2"
                    >
                      {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                      {archiving ? 'Archivage...' : 'Confirmer l\'archivage'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </>
      )}
    </AnimatePresence>
  );
}

function ClientsAdmin({ pushToast }: { pushToast: (type: 'success' | 'error', msg: string) => void }) {
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [archivedClients, setArchivedClients] = useState<ClientItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [viewingArchived, setViewingArchived] = useState(false);
  const [clientFormModal, setClientFormModal] = useState<{ isOpen: boolean; client: ClientItem | null }>({
    isOpen: false,
    client: null
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; client: ClientItem | null }>({
    isOpen: false,
    client: null
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllClients();
      setClients(data);
    } catch (err) {
      setError('Erreur lors du chargement des clients');
      pushToast('error', 'Erreur lors du chargement des clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadArchived = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getArchivedClients();
      setArchivedClients(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des clients archivés');
      pushToast('error', err instanceof Error ? err.message : 'Erreur lors du chargement des clients archivés');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewingArchived) loadArchived();
    else load();
  }, [viewingArchived]);

  const filtered = useMemo(() => {
    const f = filter.toLowerCase();
    return clients.filter(c => c.nom.toLowerCase().includes(f));
  }, [clients, filter]);

  const handleEdit = (client: ClientItem) => {
    setClientFormModal({ isOpen: true, client });
  };

  const handleNew = () => {
    setClientFormModal({ isOpen: true, client: null });
  };

  const handleDeleteClick = (client: ClientItem) => {
    setDeleteConfirm({ isOpen: true, client });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.client?.id) return;

    setLoading(true);
    setError(null);
    try {
      await deleteClient(deleteConfirm.client.id);
      pushToast('success', 'Client supprimé avec succès');
      await load();
    } catch (err) {
      setError('Erreur lors de la suppression');
      pushToast('error', 'Erreur lors de la suppression du client');
      console.error(err);
    } finally {
      setLoading(false);
      setDeleteConfirm({ isOpen: false, client: null });
    }
  };

  const handleRestoreArchived = async (client: ClientItem) => {
    if (!client.id) return;
    setLoading(true);
    setError(null);
    try {
      await unarchiveClient(client.id);
      pushToast('success', `Client « ${client.nom} » restauré avec succès`);
      await loadArchived();
    } catch (err) {
      pushToast('error', err instanceof Error ? err.message : 'Erreur lors de la restauration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Barre de recherche et actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={viewingArchived ? 'Rechercher un client archivé...' : 'Rechercher un client...'}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={viewingArchived}
          />
        </div>
        {viewingArchived ? (
          <button
            onClick={() => setViewingArchived(false)}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <ArchiveRestore className="w-4 h-4" />
            Retour aux clients actifs
          </button>
        ) : (
          <>
            <button
              onClick={() => setViewingArchived(true)}
              className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors font-medium flex items-center gap-2"
            >
              <Archive className="w-4 h-4" />
              Clients archivés
            </button>
            <button
              onClick={handleNew}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium hover:from-blue-700 hover:to-blue-600 transition-all flex items-center gap-2 shadow-md"
            >
              <Plus className="w-4 h-4" />
              Nouveau client
            </button>
          </>
        )}
        <button
          onClick={() => viewingArchived ? loadArchived() : load()}
          disabled={loading}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Liste des clients (actifs ou archivés) */}
      {loading && (viewingArchived ? archivedClients.length === 0 : clients.length === 0) ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : viewingArchived ? (
        archivedClients.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Archive className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun client archivé</p>
            <p className="text-sm text-gray-500 mt-2">Les clients archivés apparaîtront ici.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {archivedClients.map((client) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-amber-50/70 rounded-xl shadow-sm border border-amber-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-lg truncate">{client.nom}</h3>
                    <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                      <p>ID: {client.id}</p>
                      {client.secteur && <p>Secteur: {client.secteur}</p>}
                      {client.archived_at && (
                        <p className="text-amber-700">
                          Archivé le: {new Date(client.archived_at).toLocaleDateString('fr-CA')}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRestoreArchived(client)}
                    disabled={loading}
                    className="p-2 rounded-lg hover:bg-amber-100 text-amber-700 transition-colors flex items-center gap-1.5"
                    title="Restaurer le client"
                  >
                    <ArchiveRestore className="w-4 h-4" />
                    <span className="text-sm font-medium">Restaurer</span>
                  </button>
                </div>
                <Badge color="gray">Archivé</Badge>
              </motion.div>
            ))}
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {filter ? 'Aucun client trouvé' : 'Aucun client enregistré'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((client) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-lg truncate">{client.nom}</h3>
                  <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                    <p>ID: {client.id}</p>
                    {client.secteur && <p>Secteur: {client.secteur}</p>}
                    {client.taille_entreprise && <p>Taille: {client.taille_entreprise}</p>}
                    {client.nombre_employes && <p>Employés: {client.nombre_employes.toLocaleString()}</p>}
                    {client.gitrepository && (
                      <p className="truncate">
                        Dépôt:&nbsp;
                        <a
                          href={client.gitrepository}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {client.gitrepository}
                        </a>
                      </p>
                    )}
                    {client.prismewebsite && (
                      <p className="truncate">
                        Site:&nbsp;
                        <a
                          href={client.prismewebsite}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {client.prismewebsite}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <button
                    onClick={() => handleEdit(client)}
                    className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                    title="Modifier"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(client)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3">
                <Badge color={client.prismecompleted ? 'emerald' : client.prismecompleted === false ? 'gray' : 'blue'}>
                  {client.prismecompleted === true
                    ? 'PRIsME complété'
                    : client.prismecompleted === false
                      ? 'PRIsME non complété'
                      : 'Statut PRIsME à renseigner'}
                </Badge>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal de formulaire client */}
      <ClientFormModal
        isOpen={clientFormModal.isOpen}
        onClose={() => setClientFormModal({ isOpen: false, client: null })}
        client={clientFormModal.client}
        onSave={load}
        pushToast={pushToast}
      />

      {/* Modal de confirmation de suppression */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, client: null })}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le client"
        message={`Êtes-vous sûr de vouloir supprimer le client "${deleteConfirm.client?.nom}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
}

function Badge({ children, color }: { children: React.ReactNode; color: 'blue' | 'emerald' | 'gray' | 'purple' }) {
  const map: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${map[color]}`}>
      {children}
    </span>
  );
}

/** Évite d'utiliser un courriel (ou texte invalide) stocké par erreur dans photo_url comme src d'image. */
function isDisplayablePhotoUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return false;
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:image/')
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map(n => n[0]?.toUpperCase())
    .slice(0, 2)
    .join('') || '—';
  return (
    <div
      className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center text-sm font-semibold shadow-sm"
      aria-hidden
    >
      {initials}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-all ${
        checked
          ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
          : 'bg-white border-gray-300 text-gray-700'
      } hover:shadow-sm`}
      title={checked ? "Désactiver l'accès" : "Activer l'accès"}
    >
      {checked ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
      <span className="text-xs font-medium">{checked ? 'is_user: ON' : 'is_user: OFF'}</span>
    </button>
  );
}

// Droits PRIsME (sections / comités / paramètres) — liste pour affichage et menu déroulant
const DROITS_PRISME_OPTIONS: { id: string; label: string }[] = [
  { id: 'profil_defaut', label: 'Profil par défaut (toutes les sections menu)' },
  { id: 'projets', label: 'Accès Projets (TactiCE)' },
  { id: 'comite_ce', label: 'Dany CA/CE' },
  { id: 'comite_ca', label: 'Comité CA' },
  { id: 'comite_securite', label: 'Comité Sécurité' },
  { id: 'comite_prisme', label: 'Comité PRIsME' },
  { id: 'comite_rh', label: 'Comité RH • Loi 27' },
  { id: 'admin_prisme', label: 'Admin PRIsME' },
  { id: 'vector_ai', label: 'Administration vector AI (LEX)' },
  { id: 'gestion_pp', label: 'Gestion parties prenantes' },
  { id: 'commentaires', label: 'Commentaires / Amélioration continue' },
];

// Droits de base par email pour les membres BZ — ref. docs/DROITS_ET_ACCES_SECTIONS.md
const BASE_DROITS_BY_EMAIL: Record<string, string[]> = {
  'dany.bourassa@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_ce', 'comite_ca', 'comite_securite', 'comite_prisme', 'comite_rh',
    'admin_prisme', 'vector_ai', 'gestion_pp', 'commentaires',
  ],
  'jean-michel.caouette@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_prisme', 'admin_prisme', 'vector_ai', 'gestion_pp', 'commentaires',
  ],
  'jean-pierre.leclerc@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_prisme', 'admin_prisme', 'vector_ai', 'gestion_pp', 'commentaires',
  ],
  'eric.hains@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_prisme', 'comite_securite', 'admin_prisme', 'vector_ai', 'gestion_pp', 'commentaires',
  ],
  'francis.marquis@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_prisme', 'admin_prisme', 'gestion_pp', 'commentaires',
  ],
  'francois.delisle@bzinc.ca': [
    'profil_defaut', 'comite_ca', 'comite_securite', 'admin_prisme', 'gestion_pp', 'commentaires',
  ],
  'pierre-luc.fortin@bzinc.ca': [
    'projets', 'comite_prisme', 'vector_ai', 'gestion_pp', 'commentaires',
  ],
  'stephane.paradis@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_ca', 'comite_securite', 'comite_prisme', 'comite_rh', 'gestion_pp', 'commentaires',
  ],
  'vincent.bourassa@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_securite',
  ],
  'martin.carrier@bzinc.ca': [],
  'richard.delisle@bzinc.ca': ['comite_rh'],
  'emilie.quintal@bzinc.ca': ['comite_rh'],
  'melissa.pelletier@bzinc.ca': ['comite_rh'],
  'louis-alexandre.hebert@bzinc.ca': ['comite_rh'],
  'eric.durand@bzinc.ca': [
    'profil_defaut', 'projets', 'comite_ce', 'comite_ca', 'comite_securite', 'comite_prisme', 'comite_rh',
    'admin_prisme', 'vector_ai', 'gestion_pp', 'commentaires',
  ],
};

// Seuls ces utilisateurs peuvent ajouter ou enlever des droits PRIsME pour les parties prenantes BZ
const EMAILS_AUTORISES_EDIT_DROITS = ['jean-michel.caouette@bzinc.ca', 'dany.bourassa@bzinc.ca', 'eric.durand@bzinc.ca'];

function getBaseDroitsForPartiePrenante(pp: PartiePrenante | null): string[] {
  if (!pp?.email) return [];
  const base = BASE_DROITS_BY_EMAIL[pp.email.toLowerCase()];
  if (base) return [...base];
  // Membre BZ non listé dans le doc : aucun droit de base (affichage vide ou uniquement droits en base)
  return [];
}

// Modal pour créer/modifier une partie prenante
function PPFormModal({
  isOpen,
  onClose,
  pp,
  clients,
  onSave,
  pushToast
}: {
  isOpen: boolean;
  onClose: () => void;
  pp: PartiePrenante | null;
  clients: ClientItem[];
  onSave: () => void;
  pushToast: (type: 'success' | 'error', msg: string) => void;
}) {
  const [form, setForm] = useState<Partial<PartiePrenante>>({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    titre_poste: '',
    organisation: '',
    role_principal: '',
    niveau_experience: '',
    bio: '',
    photo_url: '',
    client_id: clients[0]?.id || 1,
    is_user: false,
    actif: true,
    acces_prisme_master: false,
    git_repository_url: '',
    prisme_website_url: '',
    prisme_completed: false
  });
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [droitsAjoutes, setDroitsAjoutes] = useState<string[]>([]);
  const [droitsRetires, setDroitsRetires] = useState<string[]>([]);
  const [ajouterDroitValue, setAjouterDroitValue] = useState('');
  const [removeDroitConfirm, setRemoveDroitConfirm] = useState<{ droitId: string; label: string } | null>(null);
  const { user: currentUser } = useAuth();
  const canEditDroits =
    !!currentUser?.email &&
    EMAILS_AUTORISES_EDIT_DROITS.includes(currentUser.email.toLowerCase());

  useEffect(() => {
    if (pp?.id) {
      const dp = pp.droits_prisme;
      setDroitsAjoutes(Array.isArray(dp?.ajoutes) ? (dp?.ajoutes ?? []) : []);
      setDroitsRetires(Array.isArray(dp?.retires) ? (dp?.retires ?? []) : []);
    } else {
      setDroitsAjoutes([]);
      setDroitsRetires([]);
    }
    setAjouterDroitValue('');
    setRemoveDroitConfirm(null);
  }, [pp?.id, pp?.droits_prisme]);

  useEffect(() => {
    if (pp) {
      setForm({
        prenom: pp.prenom || '',
        nom: pp.nom || '',
        email: pp.email || '',
        telephone: pp.telephone || '',
        titre_poste: pp.titre_poste || '',
        organisation: pp.organisation || '',
        role_principal: pp.role_principal || '',
        niveau_experience: pp.niveau_experience || '',
        bio: pp.bio || '',
        photo_url: pp.photo_url || '',
        client_id: pp.client_id,
        is_user: pp.is_user || false,
        actif: pp.actif !== undefined ? pp.actif : true,
        acces_prisme_master: pp.acces_prisme_master !== undefined ? pp.acces_prisme_master : false,
        git_repository_url: pp.git_repository_url || '',
        prisme_website_url: pp.prisme_website_url || '',
        prisme_completed: pp.prisme_completed !== undefined ? pp.prisme_completed : false
      });
      setPassword(''); // Ne pas pré-remplir le password pour des raisons de sécurité
      // Charger l'avatar depuis la DB si disponible
      if (pp.has_avatar && pp.id) {
        setAvatarPreview(getPartiePrenanteAvatarUrl(pp.id) + '?t=' + Date.now());
      } else if (isDisplayablePhotoUrl(pp.photo_url)) {
        setAvatarPreview(pp.photo_url!);
      } else {
        setAvatarPreview(null);
      }
      setAvatarFile(null);
    } else {
      setForm({
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        titre_poste: '',
        organisation: '',
        role_principal: '',
        niveau_experience: '',
        bio: '',
        photo_url: '',
        client_id: clients[0]?.id || 1,
        is_user: false,
        actif: true,
        git_repository_url: '',
        prisme_website_url: '',
        prisme_completed: false
      });
      setPassword('');
      setAvatarPreview(null);
      setAvatarFile(null);
    }
  }, [pp, isOpen, clients]);

  const saveDroitsPrisme = async (ajoutes: string[], retires: string[]) => {
    if (!pp?.id) return;
    try {
      await updatePartiePrenante(pp.id, { droits_prisme: { ajoutes, retires } });
      pushToast('success', 'Droits PRIsME enregistrés.');
      onSave();
    } catch (e) {
      console.error(e);
      pushToast('error', 'Erreur lors de l\'enregistrement des droits.');
    }
  };

  const handleSave = async () => {
    if (!form.prenom?.trim() || !form.nom?.trim()) {
      pushToast('error', 'Le prénom et le nom sont requis');
      return;
    }

    // Vérifier le password seulement si on active is_user (création ou activation)
    // Si on modifie un utilisateur existant qui a déjà is_user, pas besoin de password
    if (form.is_user && !password.trim()) {
      // Si c'est une création, password requis
      if (!pp?.id) {
        pushToast('error', 'Un mot de passe est requis lorsque l\'accès utilisateur est activé');
        return;
      }
      // Si c'est une modification et qu'on active is_user pour la première fois, password requis
      if (pp && !pp.is_user && form.is_user) {
        pushToast('error', 'Un mot de passe est requis lorsque l\'accès utilisateur est activé');
        return;
      }
      // Sinon, l'utilisateur a déjà is_user, on peut continuer sans nouveau password
    }

    setSaving(true);
    try {
      // Convertir l'avatar en base64 si un fichier a été sélectionné
      let avatarBase64: string | undefined = undefined;
      let avatarMimeType: string | undefined = undefined;
      
      if (avatarFile) {
        try {
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
            reader.onload = (e) => {
              const result = e.target?.result as string;
              resolve(result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(avatarFile);
          });
          avatarBase64 = await base64Promise;
          avatarMimeType = avatarFile.type || 'image/png';
        } catch (error) {
          pushToast('error', 'Erreur lors de la lecture de l\'image');
          setSaving(false);
          return;
        }
      }

      // Préparer les données à envoyer (exclure is_bz qui est calculé)
      const dataToSend: Partial<PartiePrenante> = {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email?.trim() || undefined,
        telephone: form.telephone?.trim() || undefined,
        titre_poste: form.titre_poste?.trim() || undefined,
        organisation: form.organisation?.trim() || undefined,
        role_principal: form.role_principal?.trim() || undefined,
        niveau_experience: form.niveau_experience?.trim() || undefined,
        bio: form.bio?.trim() || undefined,
        photo_url: form.photo_url?.trim() || undefined,
        client_id: form.client_id,
        is_user: form.is_user || false,
        actif: form.actif !== undefined ? form.actif : true,
        acces_prisme_master: form.acces_prisme_master !== undefined ? form.acces_prisme_master : false,
        git_repository_url: form.git_repository_url?.trim() || null,
        prisme_website_url: form.prisme_website_url?.trim() || null,
        prisme_completed: form.prisme_completed !== undefined ? form.prisme_completed : false
      };

      // Ajouter l'avatar si fourni
      if (avatarBase64) {
        dataToSend.avatar_data_base64 = avatarBase64;
        dataToSend.avatar_mime_type = avatarMimeType;
      } else if (avatarFile === null && pp?.has_avatar && !avatarPreview) {
        // Si on veut supprimer l'avatar (avatarFile est explicitement null et pas de preview)
        // Envoyer une chaîne vide pour indiquer la suppression
        dataToSend.avatar_data_base64 = '';
        dataToSend.avatar_mime_type = '';
      }

      // Ajouter le password seulement s'il est fourni (nouveau ou modification)
      if (password.trim()) {
        dataToSend.password = password.trim();
      }

      if (pp?.id) {
        await updatePartiePrenante(pp.id, dataToSend);
        pushToast('success', 'Partie prenante modifiée avec succès');
      } else {
        await createPartiePrenante(dataToSend);
        pushToast('success', 'Partie prenante créée avec succès');
      }
      onSave();
      onClose();
      setPassword(''); // Réinitialiser le password après sauvegarde
    } catch (err: any) {
      pushToast('error', err.message || (pp ? 'Erreur lors de la modification' : 'Erreur lors de la création'));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto pointer-events-auto">
              <div className="p-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">
                    {pp ? 'Modifier la partie prenante' : 'Nouvelle partie prenante'}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      value={form.prenom || ''}
                      onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                      placeholder="Prénom"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      autoFocus
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      value={form.nom || ''}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                      placeholder="Nom"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={form.email || ''}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email@example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={form.telephone || ''}
                      onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titre du poste
                    </label>
                    <input
                      type="text"
                      value={form.titre_poste || ''}
                      onChange={(e) => setForm({ ...form, titre_poste: e.target.value })}
                      placeholder="Ex: Directeur TI"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organisation
                    </label>
                    <input
                      type="text"
                      value={form.organisation || ''}
                      onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                      placeholder="Ex: BZ Inc."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rôle principal
                    </label>
                    <input
                      type="text"
                      value={form.role_principal || ''}
                      onChange={(e) => setForm({ ...form, role_principal: e.target.value })}
                      placeholder="Ex: Décideur, Consultant"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Niveau d'expérience
                    </label>
                    <select
                      value={form.niveau_experience || ''}
                      onChange={(e) => setForm({ ...form, niveau_experience: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={saving}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="Débutant">Débutant</option>
                      <option value="Intermédiaire">Intermédiaire</option>
                      <option value="Avancé">Avancé</option>
                      <option value="Expert">Expert</option>
                      <option value="Prime">Prime</option>
                    </select>
                  </div>
                </div>

                {/* Upload Avatar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Avatar (image)
                  </label>
                  <div className="space-y-3">
                    {/* Preview de l'avatar */}
                    {(avatarPreview || isDisplayablePhotoUrl(form.photo_url)) && (
                      <div className="relative inline-block">
                        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-purple-300 bg-gray-100">
                          <img
                            src={avatarPreview || (isDisplayablePhotoUrl(form.photo_url) ? form.photo_url! : '')}
                            alt="Avatar preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="absolute -top-2 -right-2 flex gap-1">
                          {/* Bouton télécharger */}
                          {pp?.has_avatar && pp.id && (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const fileName = `${form.prenom || 'user'}_${form.nom || 'avatar'}_avatar.png`.replace(/[^a-zA-Z0-9_-]/g, '_');
                                  await downloadPartiePrenanteAvatar(pp.id!, fileName);
                                  pushToast('success', 'Avatar téléchargé avec succès');
                                } catch (error: any) {
                                  pushToast('error', error.message || 'Erreur lors du téléchargement');
                                }
                              }}
                              className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                              title="Télécharger l'avatar en PNG"
                            >
                              <Download className="w-3 h-3" />
                            </button>
                          )}
                          {/* Bouton supprimer */}
                          <button
                            type="button"
                            onClick={() => {
                              setAvatarPreview(null);
                              setAvatarFile(null);
                              setForm({ ...form, photo_url: '' });
                            }}
                            className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                            title="Supprimer l'avatar"
                          >
                            <XCircle className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Input file */}
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-2 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Cliquez pour uploader</span> ou glissez-déposez
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 2MB)</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Vérifier la taille (2MB max)
                            if (file.size > 2 * 1024 * 1024) {
                              pushToast('error', 'L\'image est trop grande (max 2MB)');
                              return;
                            }
                            setAvatarFile(file);
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setAvatarPreview(event.target?.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        disabled={saving}
                      />
                    </label>

                    {/* URL de la photo (fallback) */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Ou URL de la photo (optionnel)
                      </label>
                      <input
                        type="url"
                        value={form.photo_url || ''}
                        onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                        placeholder="https://example.com/photo.jpg"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio / Description
                  </label>
                  <textarea
                    value={form.bio || ''}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    placeholder="Description de la partie prenante..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client
                  </label>
                  <select
                    value={form.client_id || 1}
                    onChange={(e) => setForm({ ...form, client_id: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={saving}
                  >
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_user || false}
                      onChange={(e) => {
                        setForm({ ...form, is_user: e.target.checked });
                        if (!e.target.checked) {
                          setPassword(''); // Réinitialiser le password si is_user est désactivé
                        }
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                      disabled={saving}
                    />
                    <span className="text-sm text-gray-700">Accès utilisateur (is_user)</span>
                  </label>
                  
                  {/* Champ mot de passe affiché seulement si is_user est coché */}
                  {form.is_user && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mot de passe {!pp?.id && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={pp?.id ? "Laisser vide pour ne pas modifier" : "Entrez le mot de passe"}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={saving}
                      />
                      {!pp?.id && (
                        <p className="text-xs text-gray-500 mt-1">
                          Le mot de passe sera automatiquement crypté avec bcrypt
                        </p>
                      )}
                      {pp?.id && (
                        <p className="text-xs text-gray-500 mt-1">
                          Laisser vide pour conserver le mot de passe actuel
                        </p>
                      )}
                    </div>
                  )}
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.acces_prisme_master || false}
                      onChange={(e) => setForm({ ...form, acces_prisme_master: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                      disabled={saving}
                    />
                    <span className="text-sm text-gray-700">Accès PRIsME Master (Oui = peut se connecter à PRIsME BZ)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.actif !== false}
                      onChange={(e) => setForm({ ...form, actif: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                      disabled={saving}
                    />
                    <span className="text-sm text-gray-700">Actif</span>
                  </label>
                  {pp && (
                    <div className="text-xs text-gray-500 pt-1">
                      {pp.is_bz ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          <Shield className="w-3 h-3" />
                          Membre BZ (client_id = 2)
                        </span>
                      ) : (
                        <span>Pour devenir membre BZ, assignez le client "BZ Inc." (ID: 2)</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Droits PRIsME — affiché pour toutes les parties prenantes BZ ; ajout/suppression réservé à Jean-Michel et Dany */}
                {pp && (pp.client_id === 2 || pp.is_bz) && (() => {
                  const baseDroits = getBaseDroitsForPartiePrenante(pp);
                  const droitsAffiches = [...baseDroits.filter((id) => !droitsRetires.includes(id)), ...droitsAjoutes];
                  const droitsAppliquesIds = new Set(droitsAffiches);
                  return (
                    <div className="space-y-2 pt-4 mt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-800">Droits PRIsME</h4>
                      <p className="text-xs text-gray-500">
                        {canEditDroits
                          ? 'Droits appliqués. Vous pouvez en ajouter ou enlever (uniquement pour Jean-Michel et Dany).'
                          : 'Droits déjà appliqués (affichage).'}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {droitsAffiches.map((droitId) => {
                          const option = DROITS_PRISME_OPTIONS.find((o) => o.id === droitId);
                          const label = option?.label ?? droitId;
                          return (
                            <span
                              key={droitId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                            >
                              <CheckCircle className="w-3 h-3 shrink-0" />
                              {label}
                              {canEditDroits && (
                                <button
                                  type="button"
                                  onClick={() => setRemoveDroitConfirm({ droitId, label })}
                                  className="ml-0.5 p-0.5 rounded hover:bg-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  title="Enlever ce droit"
                                  disabled={saving}
                                >
                                  <XCircle className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          );
                        })}
                      </div>
                      {canEditDroits && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ajouter un droit</label>
                          <select
                            value={ajouterDroitValue}
                            onChange={async (e) => {
                              const id = e.target.value;
                              setAjouterDroitValue('');
                              if (!id || droitsAppliquesIds.has(id) || !pp?.id) return;
                              const newAjoutes = [...droitsAjoutes, id];
                              const newRetires = droitsRetires.filter((r) => r !== id);
                              setDroitsAjoutes(newAjoutes);
                              setDroitsRetires(newRetires);
                              await saveDroitsPrisme(newAjoutes, newRetires);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            disabled={saving}
                          >
                            <option value="">Sélectionner un droit à ajouter...</option>
                            {DROITS_PRISME_OPTIONS.filter((o) => !droitsAppliquesIds.has(o.id)).map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Section PRIsME */}
                <div className="pt-4 mt-4 border-t-2 border-purple-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">PRIsME</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                          <GitBranch className="w-4 h-4 text-purple-600" />
                          <span>Dépôt Git PRIsME (URL)</span>
                        </div>
                      </label>
                      <input
                        type="url"
                        value={form.git_repository_url || ''}
                        onChange={(e) => setForm({ ...form, git_repository_url: e.target.value })}
                        placeholder="https://github.com/username/repo"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={saving}
                      />
                      <p className="text-xs text-gray-500 mt-1">URL du dépôt Git PRIsME de l'employé</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-purple-600" />
                          <span>Site PRIsME (URL)</span>
                        </div>
                      </label>
                      <input
                        type="url"
                        value={form.prisme_website_url || ''}
                        onChange={(e) => setForm({ ...form, prisme_website_url: e.target.value })}
                        placeholder="https://prisme-client.railway.app"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={saving}
                      />
                      <p className="text-xs text-gray-500 mt-1">URL du site PRIsME de l'employé</p>
                    </div>

                    <div>
                      <label className="flex items-center gap-3 cursor-pointer p-3 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={form.prisme_completed || false}
                          onChange={(e) => setForm({ ...form, prisme_completed: e.target.checked })}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          disabled={saving}
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <CheckCircle className={`w-5 h-5 ${form.prisme_completed ? 'text-green-600' : 'text-gray-400'}`} />
                          <div>
                            <span className="text-sm font-medium text-gray-900">PRIsME complété</span>
                            <p className="text-xs text-gray-600">Indique si le PRIsME est complété</p>
                          </div>
                        </div>
                        {form.prisme_completed && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            Complété
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                {pp && (
                  <div className="pt-2 border-t border-gray-200 space-y-1 text-xs text-gray-500">
                    {pp.date_creation && (
                      <p>Créé le: {new Date(pp.date_creation).toLocaleDateString('fr-CA')}</p>
                    )}
                    {pp.date_modification && (
                      <p>Modifié le: {new Date(pp.date_modification).toLocaleDateString('fr-CA')}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 sticky bottom-0">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.prenom?.trim() || !form.nom?.trim()}
                  className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Enregistrement...' : 'Sauvegarder'}
                </button>
              </div>
            </div>

            {typeof document !== 'undefined' &&
              document.body &&
              removeDroitConfirm &&
              createPortal(
                <div className="fixed inset-0 z-[10000]" aria-modal="true">
                  <ConfirmModal
                    isOpen={true}
                    onClose={() => setRemoveDroitConfirm(null)}
                    onConfirm={async () => {
                      const { droitId } = removeDroitConfirm;
                      const baseDroits = pp ? getBaseDroitsForPartiePrenante(pp) : [];
                      const newAjoutes = droitsAjoutes.filter((id) => id !== droitId);
                      const newRetires = baseDroits.includes(droitId)
                        ? [...droitsRetires, droitId]
                        : droitsRetires;
                      setDroitsAjoutes(newAjoutes);
                      setDroitsRetires(newRetires);
                      setRemoveDroitConfirm(null);
                      await saveDroitsPrisme(newAjoutes, newRetires);
                    }}
                    title="Enlever ce droit ?"
                    message={`Voulez-vous vraiment enlever le droit « ${removeDroitConfirm.label} » pour cette partie prenante ?`}
                    confirmText="Enlever le droit"
                    cancelText="Annuler"
                    isDangerous={true}
                  />
                </div>,
                document.body
              )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function PPAdmin({ pushToast }: { pushToast: (type: 'success' | 'error', msg: string) => void }) {
  const { user } = useAuth();
  const canMfaReset = canManageMfaReset(user?.email);
  const [mfaStatusById, setMfaStatusById] = useState<Record<number, MfaPilotStatus>>({});
  const [mfaStatusesLoaded, setMfaStatusesLoaded] = useState(false);
  const [resettingMfaEmail, setResettingMfaEmail] = useState<string | null>(null);
  const [clientId, setClientId] = useState<number | 'all'>('all');
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [items, setItems] = useState<PartiePrenante[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ppFormModal, setPPFormModal] = useState<{ isOpen: boolean; pp: PartiePrenante | null }>({
    isOpen: false,
    pp: null
  });
  const [resetPasswordModal, setResetPasswordModal] = useState<{ isOpen: boolean; pp: PartiePrenante | null }>({
    isOpen: false,
    pp: null
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; pp: PartiePrenante | null }>({
    isOpen: false,
    pp: null
  });
  const [newPassword, setNewPassword] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const cls = await getAllClients();
      setClients(cls);
      const list = await getPartiesPrenantes(clientId === 'all' ? 'all' : clientId);
      setItems(list);
    } catch (err) {
      setError('Erreur lors du chargement des données');
      pushToast('error', 'Erreur lors du chargement des parties prenantes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [clientId]);

  const loadMfaStatuses = async () => {
    if (!canMfaReset) return;
    try {
      const pilots = await fetchMfaAdminStatus();
      const map: Record<number, MfaPilotStatus> = {};
      pilots.forEach((p) => {
        // Key by id to avoid collisions when duplicate emails exist in DB
        map[p.id] = p;
      });
      setMfaStatusById(map);
      setMfaStatusesLoaded(true);
    } catch {
      /* statut MFA optionnel */
      setMfaStatusesLoaded(false);
    }
  };

  useEffect(() => {
    loadMfaStatuses();
  }, [canMfaReset, clientId]);

  const handleResetMfa = async (pp: PartiePrenante) => {
    if (!pp.id || !pp.email || !isMfaSubjectPartiePrenante(pp)) return;
    if (
      !window.confirm(
        `Réinitialiser le MFA pour ${pp.prenom} ${pp.nom} (${pp.email}) ? L'utilisateur devra reconfigurer son application à la prochaine connexion.`
      )
    ) {
      return;
    }
    setResettingMfaEmail(pp.email);
    try {
      await adminResetMfa({ userId: pp.id });
      pushToast('success', `MFA réinitialisé pour ${pp.email}`);
      await loadMfaStatuses();
    } catch (err) {
      pushToast('error', err instanceof Error ? err.message : 'Échec réinitialisation MFA');
    } finally {
      setResettingMfaEmail(null);
    }
  };

  const filtered = useMemo(() => {
    const f = filter.toLowerCase();
    return items.filter(pp =>
      `${pp.prenom} ${pp.nom} ${pp.email}`.toLowerCase().includes(f)
    );
  }, [items, filter]);

  const handleMoveClient = async (pp: PartiePrenante, newClientId: number) => {
    if (!pp.id) return;
    const prev = pp.client_id;
    setItems(arr => arr.map(x => (x.id === pp.id ? { ...x, client_id: newClientId } : x)));
    try {
      await updatePartiePrenante(pp.id, { client_id: newClientId });
      pushToast('success', 'Déplacé avec succès');
    } catch (err) {
      setItems(arr => arr.map(x => (x.id === pp.id ? { ...x, client_id: prev } : x)));
      pushToast('error', 'Erreur lors du déplacement');
      console.error(err);
    }
  };

  const handleToggleUser = async (pp: PartiePrenante) => {
    if (!pp.id) return;
    const next = !pp.is_user;
    setItems(arr => arr.map(x => (x.id === pp.id ? { ...x, is_user: next } : x)));
    try {
      await updatePartiePrenante(pp.id, { is_user: next });
      pushToast('success', next ? 'Accès activé' : 'Accès désactivé');
    } catch (err) {
      setItems(arr => arr.map(x => (x.id === pp.id ? { ...x, is_user: !next } : x)));
      pushToast('error', "Erreur lors du changement d'accès");
      console.error(err);
    }
  };

  const handleResetPasswordClick = (pp: PartiePrenante) => {
    setResetPasswordModal({ isOpen: true, pp });
    setNewPassword('');
  };

  const handleEdit = (pp: PartiePrenante) => {
    setPPFormModal({ isOpen: true, pp });
  };

  const handleNew = () => {
    setPPFormModal({ isOpen: true, pp: null });
  };

  const handleDeleteClick = (pp: PartiePrenante) => {
    setDeleteConfirm({ isOpen: true, pp });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.pp?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      await deletePartiePrenante(deleteConfirm.pp.id);
      pushToast('success', 'Partie prenante supprimée avec succès');
      await load();
    } catch (err) {
      setError('Erreur lors de la suppression');
      pushToast('error', 'Erreur lors de la suppression de la partie prenante');
      console.error(err);
    } finally {
      setLoading(false);
      setDeleteConfirm({ isOpen: false, pp: null });
    }
  };

  const handleResetPasswordConfirm = async () => {
    if (!resetPasswordModal.pp?.id || !newPassword.trim()) {
      pushToast('error', 'Le mot de passe est requis');
      return;
    }

    try {
      await updatePartiePrenante(resetPasswordModal.pp.id, { password: newPassword });
      pushToast('success', 'Mot de passe réinitialisé avec succès');
      setResetPasswordModal({ isOpen: false, pp: null });
      setNewPassword('');
    } catch (err) {
      pushToast('error', 'Erreur lors de la réinitialisation du mot de passe');
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Barre de recherche et filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Rechercher nom/email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">Tous les clients</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.nom}</option>
          ))}
        </select>
        <button
          onClick={handleNew}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2 shadow-md"
        >
          <Plus className="w-4 h-4" />
          Nouvelle partie prenante
        </button>
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Liste des parties prenantes */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {filter ? 'Aucune partie prenante trouvée' : 'Aucune partie prenante enregistrée'}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 text-sm text-gray-600">
            Affichage de <strong>{filtered.length}</strong> partie{filtered.length > 1 ? 's' : ''} prenante{filtered.length > 1 ? 's' : ''}
            {filter && items.length !== filtered.length && (
              <span> (sur {items.length} au total)</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((pp) => (
              <motion.div
                key={pp.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow ${
                  (pp as any).is_bz ? 'border-blue-300 bg-blue-50/60' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="relative flex-shrink-0">
                    {pp.has_avatar && pp.id ? (
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-purple-200 bg-gray-100">
                        <img
                          src={getPartiePrenanteAvatarUrl(pp.id)}
                          alt={`${pp.prenom} ${pp.nom}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback vers Avatar component si l'image ne charge pas
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const avatarComponent = document.createElement('div');
                              avatarComponent.innerHTML = `<div class="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-semibold text-sm">${(pp.prenom?.[0] || '')}${(pp.nom?.[0] || '')}</div>`;
                              parent.appendChild(avatarComponent.firstChild as Node);
                            }
                          }}
                        />
                      </div>
                    ) : isDisplayablePhotoUrl(pp.photo_url) ? (
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-purple-200 bg-gray-100">
                        <img
                          src={pp.photo_url!}
                          alt={`${pp.prenom} ${pp.nom}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const avatarComponent = document.createElement('div');
                              avatarComponent.innerHTML = `<div class="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-semibold text-sm">${(pp.prenom?.[0] || '')}${(pp.nom?.[0] || '')}</div>`;
                              parent.appendChild(avatarComponent.firstChild as Node);
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <Avatar name={`${pp.prenom} ${pp.nom}`} />
                    )}
                    {/* Bouton télécharger avatar (si disponible en DB) */}
                    {pp.has_avatar && pp.id && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const fileName = `${pp.prenom || 'user'}_${pp.nom || 'avatar'}_avatar.png`.replace(/[^a-zA-Z0-9_-]/g, '_');
                            await downloadPartiePrenanteAvatar(pp.id!, fileName);
                            pushToast('success', 'Avatar téléchargé avec succès');
                          } catch (error: any) {
                            pushToast('error', error.message || 'Erreur lors du téléchargement');
                          }
                        }}
                        className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors shadow-md"
                        title="Télécharger l'avatar en PNG"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {pp.prenom} {pp.nom}
                      </h3>
                      {(pp as any).is_bz && <Badge color="blue">BZ</Badge>}
                      {pp.is_user && <Badge color="emerald">USER</Badge>}
                      {pp.acces_prisme_master && <Badge color="purple">PRIsME Master</Badge>}
                      {canMfaReset && isMfaSubjectPartiePrenante(pp) && (() => {
                        const mfaSt = pp.id ? mfaStatusById[pp.id] : undefined;
                        if (mfaSt?.mfaEnabled) return <Badge color="emerald">MFA actif</Badge>;
                        if (mfaSt?.pendingSetup) return <Badge color="purple">MFA en attente</Badge>;
                        if (!mfaStatusesLoaded) return <Badge color="gray">MFA …</Badge>;
                        return <Badge color="gray">MFA off</Badge>;
                      })()}
                    </div>
                    <div className="text-sm text-gray-600 flex items-center gap-1 truncate mb-1">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{pp.email || '—'}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Client ID: {pp.client_id}
                      {pp.id != null && <span className="ml-2 text-gray-400">· PP #{pp.id}</span>}
                    </div>
                  </div>
                </div>

                {/* Section PRIsME dans la card */}
                {(pp.git_repository_url || pp.prisme_website_url || pp.prisme_completed !== undefined) && (
                  <div className="mb-4 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-semibold text-gray-700">PRIsME</span>
                      {pp.prisme_completed && (
                        <Badge color="emerald">Complété</Badge>
                      )}
                    </div>
                    <div className="space-y-1.5 text-xs">
                      {pp.git_repository_url && (
                        <div className="flex items-center gap-1.5 text-gray-600 truncate">
                          <GitBranch className="w-3 h-3 flex-shrink-0 text-purple-500" />
                          <a 
                            href={pp.git_repository_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="truncate hover:text-purple-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {pp.git_repository_url}
                          </a>
                        </div>
                      )}
                      {pp.prisme_website_url && (
                        <div className="flex items-center gap-1.5 text-gray-600 truncate">
                          <Globe className="w-3 h-3 flex-shrink-0 text-purple-500" />
                          <a 
                            href={pp.prisme_website_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="truncate hover:text-purple-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {pp.prisme_website_url}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleEdit(pp)}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-purple-50 text-purple-600 transition-colors flex items-center gap-1 text-xs"
                    title="Modifier"
                  >
                    <Pencil className="w-3 h-3" />
                    Modifier
                  </button>
                  <Toggle checked={!!pp.is_user} onChange={() => handleToggleUser(pp)} />
                  <button
                    onClick={() => handleResetPasswordClick(pp)}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-yellow-50 text-gray-700 transition-colors flex items-center gap-1 text-xs"
                    title="Réinitialiser mot de passe"
                  >
                    <KeyRound className="w-3 h-3" />
                    Reset
                  </button>
                  {canMfaReset &&
                    isMfaSubjectPartiePrenante(pp) &&
                    !!pp.id &&
                    (mfaStatusById[pp.id]?.mfaEnabled || mfaStatusById[pp.id]?.pendingSetup) && (
                    <button
                      type="button"
                      onClick={() => handleResetMfa(pp)}
                      disabled={resettingMfaEmail === pp.email}
                      className="px-3 py-1.5 rounded-lg border border-indigo-300 bg-white hover:bg-indigo-50 text-indigo-700 transition-colors flex items-center gap-1 text-xs disabled:opacity-50"
                      title="Réinitialiser MFA TOTP"
                    >
                      {resettingMfaEmail === pp.email ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Shield className="w-3 h-3" />
                      )}
                      Reset MFA
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(pp)}
                    className="px-3 py-1.5 rounded-lg border border-red-300 bg-white hover:bg-red-50 text-red-600 transition-colors flex items-center gap-1 text-xs"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3 h-3" />
                    Supprimer
                  </button>
                  <select
                    value={pp.client_id}
                    onChange={(e) => handleMoveClient(pp, parseInt(e.target.value))}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                    title="Changer de client"
                  >
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* Modal de formulaire partie prenante */}
      <PPFormModal
        isOpen={ppFormModal.isOpen}
        onClose={() => setPPFormModal({ isOpen: false, pp: null })}
        pp={ppFormModal.pp}
        clients={clients}
        onSave={load}
        pushToast={pushToast}
      />

      {/* Modal de confirmation de suppression */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, pp: null })}
        onConfirm={handleDeleteConfirm}
        title="Supprimer la partie prenante"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteConfirm.pp?.prenom} ${deleteConfirm.pp?.nom}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />

      {/* Modal de réinitialisation de mot de passe */}
      <AnimatePresence>
        {resetPasswordModal.isOpen && resetPasswordModal.pp && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setResetPasswordModal({ isOpen: false, pp: null });
                setNewPassword('');
              }}
              className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              >
                <div className="p-6 bg-blue-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-blue-900">
                        Réinitialiser le mot de passe
                      </h3>
                    </div>
                    <button
                      onClick={() => {
                        setResetPasswordModal({ isOpen: false, pp: null });
                        setNewPassword('');
                      }}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <p className="text-gray-700">
                    Entrez le nouveau mot de passe pour <strong>{resetPasswordModal.pp.prenom} {resetPasswordModal.pp.nom}</strong>:
                  </p>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Nouveau mot de passe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newPassword.trim()) {
                        handleResetPasswordConfirm();
                      }
                    }}
                    autoFocus
                  />
                </div>

                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3">
                  <button
                    onClick={() => {
                      setResetPasswordModal({ isOpen: false, pp: null });
                      setNewPassword('');
                    }}
                    className="px-6 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleResetPasswordConfirm}
                    disabled={!newPassword.trim()}
                    className="px-6 py-2 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Réinitialiser
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}







